import classNames from 'classnames'
import * as keycodes from 'keycode'
import * as React from 'react'
import { createPortal } from 'react-dom'

import { ModalFooter } from './ModalFooter'
import { ModalHeader } from './ModalHeader'

import './Modal.scss'

interface ModalProps {
  modalName?: string
  onDismiss?: () => void
  size?: 's' | 'm' | 'l' | 'xl' | 'xxl'
  sticky?: boolean
  backdrop?: boolean
  color?: boolean
  disableOutsideClick?: boolean
  animationType?: string
  backgroundImage?: string
  backgroundColor?: string
  backgroundPosition?: string
  contentOverflow?: boolean
  headerlessModal?: boolean
  className?: string
  outsideBackgroundColor?: string
  children?: React.ReactNode | undefined
}

interface ModalState {
  show: boolean
}

class Modal extends React.Component<ModalProps, ModalState> {
  public static defaultProps = {
    size: 'm',
    sticky: false,
    backdrop: true,
    color: true,
    animationType: '',
  }
  public wrapperRef: any

  constructor(props: ModalProps) {
    super(props)
    this.state = {
      show: false,
    }
  }

  public componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown)
    this.setState({ show: true })
  }

  public componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown)
    this.removeOverflow()
  }

  public componentDidUpdate(oldProps: ModalProps, oldState: ModalState) {
    if (oldState.show !== this.state.show) {
      this.handleOverflow()
    }
  }

  public render(): any {
    const { backgroundColor, backgroundImage } = this.props

    const className = classNames(
      'srprs-modal',
      `size-${this.props.size}`,
      this.props.sticky ? 'type-sticky' : 'type-dialog',
      this.props.backdrop ? 'has-backdrop' : '',
      this.props.backdrop && this.props.color ? 'color-layout' : 'color-dark',
      this.props.animationType ? 'animation-' + this.props.animationType : '',
      this.props.backgroundImage ? 'has-backgroundImage' : '',
      this.props.contentOverflow ? 'has-contentoverflow' : '',
      this.props.headerlessModal ? 'is-headerless' : '',
    )

    const renderHeader = () => {
      return React.Children.map(this.props.children, child => {
        const childElement = child as React.ReactElement<any>
        if (childElement && childElement.type === ModalHeader) {
          return React.cloneElement(childElement, {
            onDismiss: childElement.props.onDismiss || this.props.onDismiss,
          })
        }
      })
    }

    const renderChildren = () => {
      return React.Children.map(this.props.children, child => {
        const childElement = child as React.ReactElement<any>
        if (
          childElement &&
          childElement.type !== ModalHeader &&
          childElement.type !== ModalFooter
        ) {
          return childElement
        }
      })
    }

    const renderFooter = () => {
      return React.Children.map(this.props.children, child => {
        const childElement = child as React.ReactElement<any>
        if (childElement && childElement.type === ModalFooter) {
          return childElement
        }
      })
    }

    const extraStyle = {
      backgroundColor,
      backgroundImage: backgroundImage ? `url(${backgroundImage}` : undefined,
      backgroundPosition: this.props.backgroundPosition
        ? this.props.backgroundPosition
        : '',
    }

    return this.state.show
      ? createPortal(
          <div id={this.props.modalName}>
            <div className={className}>
              <article
                className={`wrapper-modal`}
                ref={this.setWrapperRef}
                style={extraStyle}
              >
                {renderHeader()}
                <div
                  className={`wrapper-inner ${
                    this.props.className ? this.props.className : ''
                  } `}
                >
                  {renderChildren()}
                </div>
                {renderFooter()}
              </article>
              <div
                style={
                  this.props.outsideBackgroundColor
                    ? { backgroundColor: this.props.outsideBackgroundColor }
                    : {}
                }
                className="srprs-modalBackdrop"
                onClick={this.simpleOutsideClick}
              />
            </div>
          </div>,
          document.getElementsByTagName('main')[0],
        )
      : null
  }

  private setWrapperRef = (node: any) => {
    this.wrapperRef = node
  }

  private handleOverflow = () => {
    if (this.state.show) {
      document.body.style.overflow = 'hidden'
    } else {
      this.removeOverflow()
    }
  }

  private removeOverflow = () => {
    document.body.style.overflow = 'auto'
  }

  private simpleOutsideClick = () => {
    if (this.props.onDismiss) {
      this.props.onDismiss()
    }
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.keyCode === keycodes.codes.esc && this.props.onDismiss) {
      this.props.onDismiss()
    }
  }
}

export { Modal }
